import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  ListItemButton,
} from '@mui/material';
import { Close as CloseIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { useUserSearch } from '../../hooks/useUserSearch';
import { addTeamMember } from '../../../../services/api/endpoints/caas';
import { useAuth } from '../../../../contexts/AuthContext';

interface AddMemberModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  teamId?: string;
  existingMemberIds: string[]; // IDs of users already in the team
}

const AddMemberModal: React.FC<AddMemberModalProps> = ({
  open,
  onClose,
  onSuccess,
  teamId,
  existingMemberIds,
}) => {
  const { user } = useAuth();
  const { users, isSearching, error: searchError, search, clear } = useUserSearch();
  const [searchQuery, setSearchQuery] = useState('');
  const [addingUserId, setAddingUserId] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Debounce search
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      clear();
      setApiError(null);
      setSuccessMessage(null);
      return;
    }

    const timer = setTimeout(() => {
      if (searchQuery.trim()) {
        search(searchQuery);
      } else {
        clear();
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, open, search, clear]);

  const handleAddMember = async (userId: string) => {
    if (!user?.token || !teamId) {
      setApiError('Missing authentication or team ID');
      return;
    }

    try {
      setAddingUserId(userId);
      setApiError(null);
      setSuccessMessage(null);
      
      await addTeamMember(user.token, teamId, userId);
      
      setSuccessMessage('Member added successfully!');
      
      // Close modal after short delay
      setTimeout(() => {
        onSuccess();
      }, 1000);
    } catch (err: any) {
      console.error('Error adding team member:', err);
      setApiError(err.message || 'Failed to add team member');
    } finally {
      setAddingUserId(null);
    }
  };

  const handleClose = () => {
    if (!addingUserId) {
      onClose();
    }
  };

  // Filter out users who are already members
  const availableUsers = users.filter(u => !existingMemberIds.includes(u.id));

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="add-member-modal-title"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '90%', md: '600px' },
          maxHeight: '80vh',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <Typography id="add-member-modal-title" variant="h6" component="h2">
            Add Team Member
          </Typography>
          <IconButton onClick={handleClose} size="small" disabled={!!addingUserId}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Search users by name or email"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            disabled={!!addingUserId}
            sx={{ mb: 2 }}
            autoFocus
          />

          {searchError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {searchError}
            </Alert>
          )}

          {isSearching && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {!isSearching && searchQuery && availableUsers.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              {users.length === 0
                ? 'No users found'
                : 'All matching users are already team members'}
            </Typography>
          )}

          {!isSearching && availableUsers.length > 0 && (
            <List>
              {availableUsers.map((u) => (
                <ListItem
                  key={u.id}
                  disablePadding
                  secondaryAction={
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={addingUserId === u.id ? <CircularProgress size={16} /> : <PersonAddIcon />}
                      onClick={() => handleAddMember(u.id)}
                      disabled={!!addingUserId}
                    >
                      Add
                    </Button>
                  }
                >
                  <ListItemButton disabled>
                    <ListItemAvatar>
                      <Avatar src={u.image_url} alt={u.display_name || u.email}>
                        {(u.display_name || u.email).charAt(0).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        u.display_name ||
                        `${u.first_name || ''} ${u.last_name || ''}`.trim() ||
                        u.email
                      }
                      secondary={u.email}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}

          {!searchQuery && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              Start typing to search for users
            </Typography>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default AddMemberModal;
